import React, { useEffect, useRef, useState, useCallback } from "react";
import "./MapPage.css";

// Kakao Map 스크립트를 단 한 번만 로드하기 위한 싱글톤 함수
const loadKakaoMapScript = (() => {
  let promise;
  return (apiKey) => {
    if (promise) return promise;
    promise = new Promise((resolve, reject) => {
      if (document.querySelector(`script[src*="dapi.kakao.com"]`)) {
        if (window.kakao && window.kakao.maps) {
          resolve();
        } else {
          document
            .querySelector(`script[src*="dapi.kakao.com"]`)
            .addEventListener("load", resolve);
        }
        return;
      }
      const script = document.createElement("script");
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false&libraries=services`;
      script.async = true;
      script.onload = () => {
        if (window.kakao && window.kakao.maps) {
          window.kakao.maps.load(() => resolve());
        } else {
          reject(new Error("Kakao Maps SDK failed to load."));
        }
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return promise;
  };
})();

const MapPage = ({ clubs, selectedLocation, isNearbyActive, onSelectClub, height = "70vh" }) => {
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const userMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const markersCacheRef = useRef(new Map()); // 클럽 id별 마커 캐시
  const coordsCacheRef = useRef(new Map());  // 클럽 id별 좌표 캐시
  const clubsRef = useRef(clubs);
  const [userCoords, setUserCoords] = useState(null);
  const [hasUserMovedMap, setHasUserMovedMap] = useState(false);

  // clubs가 업데이트될 때마다 ref도 갱신
  useEffect(() => {
    clubsRef.current = clubs;
  }, [clubs]);

  // 유틸리티 함수 – 평균 좌표 계산
  const calculateAverageCoordinates = (coordinates) => {
    if (coordinates.length === 0) return { lat: 37.5665, lng: 126.9780 };
    const total = coordinates.reduce(
      (acc, curr) => ({ lat: acc.lat + curr.getLat(), lng: acc.lng + curr.getLng() }),
      { lat: 0, lng: 0 }
    );
    return { lat: total.lat / coordinates.length, lng: total.lng / coordinates.length };
  };

  // 지도 중심 조정 함수
  const handleCentering = useCallback((map) => {
    // 바텀 시트 높이에 맞춰 지도 중심을 조정하는 함수
    const adjustMapForBottomSheet = (targetCoords, level) => {
      const container = document.getElementById("map");
      if (container) {
        const mapHeight = container.offsetHeight;
        const panY = mapHeight * 0.55 / 2; // 바텀시트 55% 높이에 맞춘 조정
        
        map.setLevel(level);
        map.panTo(targetCoords);
        
        // 약간의 딜레이 후 Y축 조정 (지도 이동이 완료된 후)
        setTimeout(() => {
          map.panBy(0, panY);
        }, 100);
      }
    };

    if (isNearbyActive && userCoords) {
      const userLatLng = new window.kakao.maps.LatLng(userCoords.lat, userCoords.lng);
      adjustMapForBottomSheet(userLatLng, 3);
      return;
    }
    if (selectedLocation && !hasUserMovedMap) {
      const filteredClubs = clubs.filter(club => club.location === selectedLocation);
      const coordsArray = filteredClubs.map(club => coordsCacheRef.current.get(club.id.toString())).filter(Boolean);
      if (coordsArray.length > 0) {
        const avgCoords = calculateAverageCoordinates(coordsArray);
        const avgLatLng = new window.kakao.maps.LatLng(avgCoords.lat, avgCoords.lng);
        adjustMapForBottomSheet(avgLatLng, 3);
      }
      return;
    }
    if (!hasUserMovedMap) {
      const allCoords = clubs.map(club => coordsCacheRef.current.get(club.id.toString())).filter(Boolean);
      if (allCoords.length > 0) {
        const avgCoords = calculateAverageCoordinates(allCoords);
        const avgLatLng = new window.kakao.maps.LatLng(avgCoords.lat, avgCoords.lng);
        adjustMapForBottomSheet(avgLatLng, 9);
      }
    }
  }, [isNearbyActive, userCoords, selectedLocation, hasUserMovedMap, clubs]);

  // 클럽 마커 생성 함수
  const createClubMarker = useCallback((club, coords) => {
    const container = document.createElement("div");
    container.className = "custom-marker";
    const icon = document.createElement("img");
    icon.src = "/Images/FireIcon.png";
    icon.className = "fire-icon";
    const label = document.createElement("div");
    label.className = "marker-label";
    label.textContent = club.name;
    container.appendChild(icon);
    container.appendChild(label);
    container.setAttribute("role", "button");
    container.setAttribute("tabindex", "0");

    const onClick = () => {
      if (onSelectClub) {
        onSelectClub(club);
      }
    };
    container.addEventListener("click", onClick);
    
    const customOverlay = new window.kakao.maps.CustomOverlay({
      map: mapRef.current,
      position: coords,
      content: container,
      clickable: true,
    });

    customOverlay._cleanup = () => container.removeEventListener("click", onClick);
    markersCacheRef.current.set(club.id.toString(), customOverlay);
  }, [onSelectClub]);

  // 마커 업데이트 및 지도 중심 조정 함수
  const updateMarkersAndCenter = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const geocoder = geocoderRef.current;
    if (!geocoder) return;

    const desiredClubIds = new Set(clubs.map(c => c.id.toString()));
    markersCacheRef.current.forEach((overlay, clubId) => {
      if (!desiredClubIds.has(clubId)) {
        if (overlay._cleanup) overlay._cleanup();
        overlay.setMap(null);
        markersCacheRef.current.delete(clubId);
      }
    });

    clubs.forEach(club => {
      const clubKey = club.id.toString();
      if (markersCacheRef.current.has(clubKey)) return;
      if (club.address) {
        if (coordsCacheRef.current.has(clubKey)) {
          createClubMarker(club, coordsCacheRef.current.get(clubKey));
        } else {
          geocoder.addressSearch(club.address, (result, status) => {
            if (!clubsRef.current.some(c => c.id.toString() === clubKey)) return;
            if (status === window.kakao.maps.services.Status.OK && result[0]) {
              const coords = new window.kakao.maps.LatLng(result[0].y, result[0].x);
              coordsCacheRef.current.set(clubKey, coords);
              createClubMarker(club, coords);
            }
          });
        }
      }
    });

    handleCentering(map);
  }, [clubs, createClubMarker, handleCentering]);

  // 사용자 위치 감시 함수
  const watchUserPosition = useCallback((map) => {
    if (!navigator.geolocation || !isNearbyActive) return;
    
    
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const userLatLng = new window.kakao.maps.LatLng(latitude, longitude);
        
        setUserCoords({ lat: latitude, lng: longitude });

        if (!userMarkerRef.current) {
          userMarkerRef.current = new window.kakao.maps.Marker({ map, position: userLatLng });
        } else {
          userMarkerRef.current.setPosition(userLatLng);
        }
        if (isNearbyActive) {
          map.setLevel(3);
          map.panTo(userLatLng);
        }
      },
              (err) => {},
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
    );
  }, [isNearbyActive]);

  // 선택한 지역 변경 시 지도 중심 재조정
  useEffect(() => {
    setHasUserMovedMap(false);
  }, [selectedLocation]);

  // isNearbyActive 변경 시 위치 감시 시작/중지
  useEffect(() => {
    if (!mapRef.current) return;
    
    if (isNearbyActive) {
      
      watchUserPosition(mapRef.current);
    } else {
      
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
        userMarkerRef.current = null;
      }
    }
  }, [isNearbyActive, watchUserPosition]);

  // 지도 초기화 (최초 1회 실행)
  useEffect(() => {
    const apiKey = process.env.REACT_APP_KAKAO_MAP_API_KEY;
    if (!apiKey) {
      console.error("Kakao Map API key is missing.");
      return;
    }

    loadKakaoMapScript(apiKey)
      .then(() => {
        const container = document.getElementById("map");
        if (!container) return;
        const options = {
          center: new window.kakao.maps.LatLng(37.5665, 126.9780),
          level: 8,
          zoomControl: true,
          scrollwheel: true,
        };
        const map = new window.kakao.maps.Map(container, options);
        mapRef.current = map;
        geocoderRef.current = new window.kakao.maps.services.Geocoder();

        // 바텀시트가 0.55(=55%)를 차지하므로, 맵 높이의 0.55/2만큼 위로 이동
        setTimeout(() => {
          const mapHeight = container.offsetHeight;
          const panY = mapHeight * 0.55 / 2;
          map.panBy(0, panY);
        }, 300); // 지도 렌더링 후 약간의 딜레이를 줌

        const onDragStart = () => setHasUserMovedMap(true);
        const onZoomChanged = () => setHasUserMovedMap(true);

        window.kakao.maps.event.addListener(map, "dragstart", onDragStart);
        window.kakao.maps.event.addListener(map, "zoom_changed", onZoomChanged);

        // isNearbyActive가 true일 때만 위치 감시 시작
        if (isNearbyActive) {
          watchUserPosition(map);
        }

        return () => {
          window.kakao.maps.event.removeListener(map, "dragstart", onDragStart);
          window.kakao.maps.event.removeListener(map, "zoom_changed", onZoomChanged);
        };
      })
      .catch((error) => console.error("Error loading Kakao Map:", error));

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      const currentMarkers = markersCacheRef.current;
      currentMarkers.forEach((overlay) => {
        if (overlay._cleanup) overlay._cleanup();
        overlay.setMap(null);
      });
      currentMarkers.clear();
    };
  }, [watchUserPosition]);

  // clubs 데이터가 변경될 때마다 마커와 중심 업데이트
  useEffect(() => {
    if (!mapRef.current) return;
    updateMarkersAndCenter();
  }, [clubs, updateMarkersAndCenter]);

  return (
    <div>
      <div id="map" style={{ width: "100%", height: height }} />
    </div>
  );
};

export default MapPage;
